<template>
    <div class="padding-normal bg-green-300">
        <div class="lg:grid lg:grid-cols-2">
            <div class="relative flex items-center lg:justify-end">
                <div class="relative ml-3 mr-6 lg:mx-0 lg:h-[596px] lg:w-[801px]">
                    <div
                        class="box-shadow-size absolute bottom-0 right-0 rounded-br-[40px] border-2 border-green-800 bg-green-800"></div>
                    <NuxtLink
                        :to="
                            button.buttonType === 'external'
                                ? button.externalUrl
                                : '/' + (button.internalLink?._ref ? shopwareSlugs[button.internalLink._ref] : '#')
                        ">
                        <NuxtImg
                            v-if="image"
                            :src="useImageUrl(image).format('webp').url()"
                            class="relative z-10 h-full w-[831px] object-cover px-6 py-6"
                            :alt="image.alt"
                            sizes="100vw sm:50vw md:400px" />
                        <div
                            class="absolute bottom-10 left-0 right-0 flex flex-col text-center text-white lg:bottom-20">
                            <p
                                class="z-20 font-monument-extended text-base transition-all group-hover:text-green-500 lg:text-2xl">
                                {{ imageText }}
                            </p>
                            <p
                                class="z-20 mx-auto mt-1 w-max border-b-2 font-mori text-sm font-semibold transition-all group-hover:border-green-500 group-hover:text-green-500">
                                {{ button.title }}
                            </p>
                        </div>
                    </NuxtLink>
                </div>
            </div>

            <div class="mt-10 flex flex-col justify-center pl-10 lg:mt-0 lg:pl-[107px] lg:pr-0">
                <div class="pt-6">
                    <h2
                        v-if="title"
                        class="alt-title max-w-[280px] whitespace-pre-wrap !text-[35px] !leading-[46px] lg:max-w-[460px] lg:text-5xl lg:!leading-[80px]">
                        {{ titleWithBreak(title) }}
                    </h2>
                </div>

                <div class="relative mt-10">
                    <div
                        ref="container"
                        class="keen-slider slider-trends min-h-[260px] w-full lg:min-h-[400px]">
                        <div
                            v-for="(item, index) in reversedProducts"
                            :key="item._key"
                            :class="['keen-slider__slide number-slide' + index]"
                            class="mx-2 min-w-[182px] max-w-[182px] bg-white lg:min-w-[282px] lg:max-w-[282px]">
                            <ShopwareProduct
                                v-if="item && item.id"
                                big
                                :showButtons="false"
                                :showLabels="showLabels"
                                :padding="true"
                                :product="item" />
                        </div>
                        <div :class="['keen-slider__slide number-slide']"></div>
                    </div>
                    <button
                        id="inspiration-prev-button"
                        :disabled="currentSlide === 0"
                        class="slider-prev-brand-highlight absolute left-8 top-[40%] z-10 -translate-y-1/2 transform bg-green-300 p-3 duration-300 hover:bg-green-500 lg:top-[50%]"
                        @click="prevSlide"
                        v-show="currentSlide > 0">
                        <ArrowLeftIcon
                            class="logo-slider-prev size-4 cursor-pointer stroke-[2px] text-black lg:size-6"
                            :class="{
                                'pointer-events-none': currentSlide === 0,
                            }"
                            :aria-label="snippets['headless.slider.prev']" />
                    </button>
                    <button
                        id="inspiration-next-button"
                        :disabled="currentSlide === totalSlides - 1"
                        class="slider-next-brand-highlight absolute right-16 top-[40%] z-10 -translate-y-1/2 transform bg-green-300 p-3 duration-300 hover:bg-green-500 lg:top-[50%]"
                        @click="nextSlide">
                        <ArrowRightIcon
                            class="logo-slider-next size-4 cursor-pointer stroke-[2px] text-black lg:size-6"
                            :class="{
                                'pointer-events-none': currentSlide === totalSlides - 1,
                            }"
                            :aria-label="snippets['headless.slider.next']" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';
    import { ref, computed, onMounted } from 'vue';
    import { useApiClient } from '~/src/apiClient';
    import { useKeenSlider } from 'keen-slider/vue';
    const { snippets } = useSnippetsStore();

    const props = defineProps({
        image: {
            type: Object,
            required: true,
        },
        imageText: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        button: {
            type: Object,
            required: true,
        },
        showLabels: {
            type: Boolean,
            default: false,
        },
        ProductSearch: {
            type: Object,
            required: true,
        },
    });

    let currentSlide = ref(0);
    let totalSlides = ref(0);
    let products = ref([]);
    const { apiClient } = useApiClient();

    const slideIndex = slider => {
        currentSlide.value = slider.track.details.rel;
    };

    const onSliderCreated = slider => {
        if (slider.track.details) {
            totalSlides.value = slider.track.details.slides.length - 1; // Subtract 1 to account for the extra slide
        }
    };

    const ids = ref(props.button.internalLink?._ref ? props.button.internalLink._ref : '');

    const shopwareSlugs = await useGetShopwarePageById(ids);

    async function fetchProductsByIds(ids) {
        if (ids.length === 0) {
            products.value = [];
            return;
        }

        try {
            const response = await apiClient.invoke('readProduct post /product', {
                body: {
                    filter: [
                        {
                            type: 'equalsAny',
                            field: 'id',
                            value: ids,
                        },
                        {
                            type: 'equals',
                            field: 'active',
                            value: true,
                        },
                    ],
                    sort: [],
                },
                headers: {
                    'sw-include-seo-urls': 'true',
                },
            });

            if (response.data && response.data.elements) {
                products.value = response.data.elements.filter(product => product && product.id);
            } else {
                products.value = [];
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            products.value = products.value.filter(product => product && product.id);
        }
    }

    const productIds = computed(() => {
        return props.ProductSearch?.map(product => product.id);
    });

    const reversedProducts = computed(() => {
        return products.value.slice().reverse();
    });

    const fetch = async () => {
        await fetchProductsByIds(productIds.value);
    };

    onMounted(async () => {
        await fetch();
    });

    function titleWithBreak(value) {
        const words = value.split(' ');
        if (words.length > 2) {
            return words.slice(0, 2).join(' ') + '\n' + words.slice(2).join(' ');
        }
        return value;
    }

    // Horizontal scroll keen slider
    const WheelControls = slider => {
        let touchTimeout;
        let position;
        let wheelActive;

        function dispatch(e, name) {
            position.x -= e.deltaX;
            position.y -= e.deltaY;
            slider.container.dispatchEvent(
                new CustomEvent(name, {
                    detail: {
                        x: position.x,
                        y: position.y,
                    },
                }),
            );
        }

        function wheelStart(e) {
            position = {
                x: e.pageX,
                y: e.pageY,
            };
            dispatch(e, 'ksDragStart');
        }

        function wheel(e) {
            dispatch(e, 'ksDrag');
        }

        function wheelEnd(e) {
            dispatch(e, 'ksDragEnd');
        }

        function eventWheel(e) {
            const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);

            if (isHorizontalScroll) {
                e.preventDefault();
                if (!wheelActive) {
                    wheelStart(e);
                    wheelActive = true;
                }
                wheel(e);
                clearTimeout(touchTimeout);
                touchTimeout = setTimeout(() => {
                    wheelActive = false;
                    wheelEnd(e);
                }, 50);
            }
        }

        slider.on('created', () => {
            slider.container.addEventListener('wheel', eventWheel, {
                passive: false,
            });
        });
    };

    const navigation = slider => {
        slider.on('created', () => {
            let nextSlideButton = document.getElementById('inspiration-next-button');
            let prevSlideButton = document.getElementById('inspiration-prev-button');

            const next = () => {
                slider.next();
            };

            const prev = () => {
                slider.prev();
            };

            nextSlideButton.addEventListener('click', next);
            prevSlideButton.addEventListener('click', prev);
        });
    };
    const dataLoaded = ref(false);
    onMounted(() => {
        dataLoaded.value = true;
    });

    const UpdateSlider = slider => {
        let touchTimeout;

        const waitForData = setInterval(() => {
            if (dataLoaded.value) {
                clearInterval(waitForData);
                slider.update();
            }
        }, 1000);
    };
    // Keen slider
    const [container, slider] = useKeenSlider(
        {
            initial: currentSlide.value,
            slideChanged: s => {
                currentSlide.value = s.track.details.rel;
            },
            loop: false,
            rubberband: false,
            slides: { perView: 3, spacing: 16 },
            mode: 'free-snap',
            breakpoints: {
                '(min-width: 0px)': {
                    slides: { perView: 2, spacing: 16 },
                },
                '(min-width: 768px)': {
                    slides: { perView: 3.5, spacing: 16 },
                },
                '(min-width: 1024px)': {
                    slides: { perView: 2, spacing: 16 },
                },
                '(min-width: 1280px)': {
                    slides: { perView: 2.8, spacing: 16 },
                },
            },
        },
        [WheelControls, navigation, UpdateSlider],
    );

    const nextSlide = () => {
        if (slider) {
            slider.next();
        }
    };

    const prevSlide = () => {
        if (slider) {
            slider.prev();
        }
    };
</script>

<style scoped>
    .box-shadow-size {
        width: calc(100% - 44px);
        height: calc(100% - 44px);
    }
    .swiper-button-next::after {
        display: none;
    }
    .gray-arrow {
        filter: grayscale(100%);
    }

    .alt-title {
        white-space: pre-wrap;
    }

    .boxed-slider .keen-slider__slide {
        margin: 0 8px; /* Adjust the margin as needed */
    }
</style>
